import { useMemo } from 'react';
import {
  hasPaywallWebViewBridge,
  PAYWALL_WEBVIEW_MESSAGE_HANDLER_NAMES,
} from '@/utils/webViewAccess';

/**
 * Hook для работы с WebView через postMessage
 * Используется для интеграции с мобильными приложениями
 */

export interface WebViewMessage {
  type: 'handoff' | 'load_success' | 'pay_failed' | 'pay_success' | 'pay_cancelled' | 'failed' | 'restore_failed' | 'restore_success';
  token?: string;
  error?: string;
  paymentMethod?: WebViewPaymentMethod;
}

export type WebViewPaymentMethod = 'card' | 'applepay';

/**
 * Проверяет, работает ли приложение в WebView
 */
export function isInWebView(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    hasPaywallWebViewBridge()
    // Обычный WebView (iframe)
    || window.parent !== window
  );
}

/**
 * Определяет тип WebView
 */
export function getWebViewType(): 'react-native' | 'swift-webkit' | 'iframe' | 'browser' | 'none' {
  if (typeof window === 'undefined') return 'none';

  if ((window as unknown as Record<string, unknown>).ReactNativeWebView) {
    return 'react-native';
  }

  const webkit = (window as unknown as Record<string, unknown>).webkit as Record<string, unknown> | undefined;
  const messageHandlers = webkit?.messageHandlers as Record<string, unknown> | undefined;
  if (PAYWALL_WEBVIEW_MESSAGE_HANDLER_NAMES.some((handlerName) => messageHandlers?.[handlerName])) {
    return 'swift-webkit';
  }

  if (window.parent !== window) {
    return 'iframe';
  }

  return 'browser';
}

/**
 * Отправляет сообщение в родительское окно (мобильное приложение)
 */
export function sendMessage(message: WebViewMessage): void {
  if (typeof window === 'undefined') {
    console.log('WebView message (not client-side):', message);
    return;
  }

  try {
    // 1. Проверяем React Native WebView
    if ((window as unknown as Record<string, unknown>).ReactNativeWebView) {
      ((window as unknown as Record<string, unknown>).ReactNativeWebView as { postMessage: (msg: string) => void }).postMessage(JSON.stringify(message));
      console.log('WebView message sent (ReactNative):', message);
      return;
    }

    // 2. Проверяем Swift WebKit WebView
    const webkit = (window as unknown as Record<string, unknown>).webkit as Record<string, unknown> | undefined;
    const messageHandlers = webkit?.messageHandlers as Record<string, unknown> | undefined;

    if (messageHandlers?.paywallHandler) {
      (messageHandlers.paywallHandler as { postMessage: (msg: WebViewMessage) => void }).postMessage(message);
      console.log('WebView message sent (Swift WebKit):', message);
      return;
    }

    // 3. Проверяем другие возможные Swift WebView handlers
    if (messageHandlers) {
      for (const handlerName of PAYWALL_WEBVIEW_MESSAGE_HANDLER_NAMES.slice(1)) {
        if (messageHandlers[handlerName]) {
          (messageHandlers[handlerName] as { postMessage: (msg: WebViewMessage) => void }).postMessage(message);
          console.log(`WebView message sent (Swift WebKit - ${handlerName}):`, message);
          return;
        }
      }
    }

    // 4. Fallback для обычного WebView (iframe)
    if (window.parent !== window) {
      window.parent.postMessage(message, '*');
      console.log('WebView message sent (parent iframe):', message);
      return;
    }

    // 5. Отладка в браузере
    console.log('WebView message (not in WebView - browser mode):', message);
  }
  catch (error) {
    console.error('Failed to send WebView message:', error);
  }
}

/**
 * Отправляет handoff сообщение с токеном
 */
export function sendHandoff(token: string): void {
  sendMessage({
    type: 'handoff',
    token,
  });
}

/**
 * Сообщает, что paywall успешно прошёл начальную проверку загрузки.
 */
export function sendLoadSuccess(): void {
  sendMessage({
    type: 'load_success',
  });
}

/**
 * Отправляет сообщение об ошибке платежа
 */
export function sendPaymentFailed(error?: string): void {
  sendMessage({
    type: 'pay_failed',
    error,
  });
}

/**
 * Отправляет сообщение об успешной оплате
 */
export function sendPaymentSuccess(paymentMethod?: WebViewPaymentMethod): void {
  sendMessage({
    type: 'pay_success',
    ...(paymentMethod ? { paymentMethod } : {}),
  });
}

/**
 * Отправляет сообщение об отмене подписки
 */
export function sendPaymentCancelled(): void {
  sendMessage({
    type: 'pay_cancelled',
  });
}

/**
 * Отправляет сообщение об общей ошибке (например, проблемы с бэкендом)
 */
export function sendFailed(error?: string): void {
  sendMessage({
    type: 'failed',
    error,
  });
}

/**
 * Отправляет сообщение об неудачном восстановлении подписки
 */
export function sendRestoreFailed(): void {
  sendMessage({
    type: 'restore_failed',
  });
}

/**
 * Отправляет сообщение об успешном восстановлении подписки
 */
export function sendRestoreSuccess(): void {
  sendMessage({
    type: 'restore_success',
  });
}

export function useWebViewMessaging() {
  const isWebView = useMemo(() => isInWebView(), []);
  const webViewType = useMemo(() => getWebViewType(), []);

  return {
    isWebView,
    getWebViewType: webViewType,
    sendMessage,
    sendHandoff,
    sendLoadSuccess,
    sendPaymentFailed,
    sendPaymentSuccess,
    sendPaymentCancelled,
    sendFailed,
    sendRestoreFailed,
    sendRestoreSuccess,
  };
}
